/**
 * GDPR/ePrivacy Compliant Cookie Manager
 * Handles cookie blocking, consent management, and audit trails
 * For Macedonia and EU compliance (2025 standards)
 */

class CookieManager {
  constructor() {
    this.consentVersion = '1.0';
    this.consentTimestamp = null;
    this.consentSettings = {
      necessary: true, // Always true, cannot be disabled
      analytics: false,
      marketing: false,
      functional: false
    };
    this.auditTrail = [];
    this.blockedScripts = new Set();
    this.init();
  }

  init() {
    // Block all non-essential cookies and scripts immediately
    this.blockThirdPartyScripts();
    this.loadSavedConsent();
  }

  /**
   * Block third-party scripts until consent is given
   */
  blockThirdPartyScripts() {
    // Block Google Analytics
    window['ga-disable-GA_MEASUREMENT_ID'] = true;
    
    // Block other tracking scripts by intercepting script loading
    const originalCreateElement = document.createElement;
    document.createElement = function(tagName) {
      const element = originalCreateElement.call(document, tagName);
      
      if (tagName.toLowerCase() === 'script') {
        const originalSetAttribute = element.setAttribute;
        element.setAttribute = function(name, value) {
          if (name === 'src' && this.shouldBlockScript(value)) {
            this.blockedScripts.add(value);
            console.log('🍪 Blocked script until consent:', value);
            return; // Don't set src, effectively blocking the script
          }
          return originalSetAttribute.call(element, name, value);
        }.bind(this);
      }
      
      return element;
    }.bind(this);
  }

  /**
   * Determine if a script should be blocked
   */
  shouldBlockScript(src) {
    if (!src) return false;
    
    const blockPatterns = [
      'google-analytics.com',
      'googletagmanager.com',
      'facebook.net',
      'facebook.com/tr',
      'doubleclick.net',
      'googlesyndication.com',
      'adsystem.amazon.com',
      'ads.yahoo.com',
      'analytics.tiktok.com',
      'connect.facebook.net',
      'hotjar.com',
      'crazyegg.com',
      'mouseflow.com'
    ];
    
    return blockPatterns.some(pattern => src.includes(pattern));
  }

  /**
   * Load saved consent from localStorage
   */
  loadSavedConsent() {
    const savedConsent = localStorage.getItem('cookieConsent');
    const savedTimestamp = localStorage.getItem('cookieConsentTimestamp');
    const savedVersion = localStorage.getItem('cookieConsentVersion');
    
    if (savedConsent && savedTimestamp && savedVersion === this.consentVersion) {
      // Check if consent is still valid (max 13 months per GDPR)
      const consentAge = Date.now() - parseInt(savedTimestamp);
      const maxAge = 13 * 30 * 24 * 60 * 60 * 1000; // 13 months
      
      if (consentAge < maxAge) {
        this.consentSettings = JSON.parse(savedConsent);
        this.consentTimestamp = parseInt(savedTimestamp);
        this.updateGoogleConsent();
        this.enableApprovedCookies();
        return true;
      }
    }
    
    return false;
  }

  /**
   * Save consent with full audit trail
   */
  saveConsent(settings, userAction = 'unknown') {
    const timestamp = Date.now();
    this.consentSettings = { ...settings };
    this.consentTimestamp = timestamp;
    
    // Save to localStorage
    localStorage.setItem('cookieConsent', JSON.stringify(settings));
    localStorage.setItem('cookieConsentTimestamp', timestamp.toString());
    localStorage.setItem('cookieConsentVersion', this.consentVersion);

    this.updateGoogleConsent();
    
    // Create audit record
    const auditRecord = {
      timestamp,
      action: userAction,
      consent: { ...settings },
      version: this.consentVersion,
      userAgent: navigator.userAgent,
      url: window.location.href,
      ip: 'client-side', // Would need server-side logging for real IP
      sessionId: this.getSessionId()
    };
    
    this.auditTrail.push(auditRecord);
    localStorage.setItem('cookieAuditTrail', JSON.stringify(this.auditTrail));
    
    // Enable approved cookies
    this.enableApprovedCookies();
    
    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: settings }));
    
    console.log('🍪 Consent saved:', auditRecord);
  }

  /**
   * Enable cookies based on user consent
   */
  enableApprovedCookies() {
    if (this.consentSettings.analytics) {
      this.enableAnalytics();
    }
    
    if (this.consentSettings.marketing) {
      this.enableMarketing();
    }
    
    if (this.consentSettings.functional) {
      this.enableFunctional();
    }
  }

  /**
   * Keep Google Consent Mode in sync with the visitor's preferences.
   */
  updateGoogleConsent() {
    if (typeof window.gtag !== 'function') return;

    window.gtag('consent', 'update', {
      analytics_storage: this.consentSettings.analytics ? 'granted' : 'denied',
      ad_storage: this.consentSettings.marketing ? 'granted' : 'denied',
      ad_user_data: this.consentSettings.marketing ? 'granted' : 'denied',
      ad_personalization: this.consentSettings.marketing ? 'granted' : 'denied'
    });
  }

  /**
   * Enable Google Analytics
   */
  enableAnalytics() {
    this.updateGoogleConsent();
    console.log('🍪 Analytics enabled');
  }

  /**
   * Enable marketing cookies
   */
  enableMarketing() {
    // Enable Facebook Pixel, Google Ads, etc.
    console.log('🍪 Marketing cookies enabled');
  }

  /**
   * Enable functional cookies
   */
  enableFunctional() {
    // Enable preferences, chat widgets, etc.
    console.log('🍪 Functional cookies enabled');
  }

  /**
   * Withdraw consent (user right under GDPR)
   */
  withdrawConsent() {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    
    this.saveConsent(onlyNecessary, 'withdrawal');
    
    // Clear non-essential cookies
    this.clearNonEssentialCookies();
    
    console.log('🍪 Consent withdrawn, non-essential cookies cleared');
  }

  /**
   * Clear non-essential cookies
   */
  clearNonEssentialCookies() {
    const cookies = document.cookie.split(';');
    const essentialPrefixes = ['jwtToken', 'userEmail', 'userFullName', 'userRole', 'selectedLanguage'];
    
    cookies.forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      const isEssential = essentialPrefixes.some(prefix => name.startsWith(prefix));
      
      if (!isEssential) {
        // Clear cookie for current domain
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        // Clear for parent domain
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        console.log('🍪 Cleared cookie:', name);
      }
    });
  }

  /**
   * Get current consent status
   */
  getConsent() {
    return {
      settings: { ...this.consentSettings },
      timestamp: this.consentTimestamp,
      version: this.consentVersion,
      hasConsent: this.consentTimestamp !== null
    };
  }

  /**
   * Get session ID for audit trail
   */
  getSessionId() {
    let sessionId = sessionStorage.getItem('cookieSessionId');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('cookieSessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Get detailed cookie information for disclosure
   */
  getCookieDetails() {
    return {
      necessary: [
        {
          name: 'jwtToken',
          purpose: 'User authentication and session management',
          duration: '7 days',
          provider: 'Spomen.mk',
          type: 'HTTP Cookie'
        },
        {
          name: 'selectedLanguage',
          purpose: 'Remember user language preference',
          duration: 'Session',
          provider: 'Spomen.mk',
          type: 'Local Storage'
        }
      ],
      analytics: [
        {
          name: '_ga, _ga_*',
          purpose: 'Google Analytics - website usage statistics',
          duration: '2 years',
          provider: 'Google Inc.',
          type: 'HTTP Cookie',
          thirdParty: 'Google Analytics'
        }
      ],
      marketing: [
        {
          name: '_fbp, _fbc',
          purpose: 'Facebook Pixel - advertising and conversion tracking',
          duration: '3 months',
          provider: 'Meta Platforms Inc.',
          type: 'HTTP Cookie',
          thirdParty: 'Facebook'
        }
      ],
      functional: [
        {
          name: 'preferences',
          purpose: 'Remember user interface preferences',
          duration: '1 year',
          provider: 'Spomen.mk',
          type: 'Local Storage'
        }
      ]
    };
  }

  /**
   * Export audit trail for compliance documentation
   */
  exportAuditTrail() {
    return {
      auditTrail: [...this.auditTrail],
      exportDate: new Date().toISOString(),
      version: this.consentVersion
    };
  }
}

// Create singleton instance
const cookieManager = new CookieManager();

export default cookieManager;
